/**
 * Diaporama du mode Publicité d'Aerith : forme d'une diapositive, valeurs d'origine et calcul des temps.
 * Rien d'autre que du calcul, pour pouvoir le tester seul (diaporama.test.js).
 *
 * Une diapositive (même forme que celle que vérifie le serveur, PUT /api/publicite) :
 *   {id, actif, duree (s), video_entiere, assombrir (0 à 90 %),
 *    fond: {type: "couleur" | "anime" | "image" | "video", couleur, anime, asset_id, url?, nom?},
 *    textes: [{id, texte, debut (s), fin (s ou null : jusqu'au bout), position, taille, couleur, animation, cadre}]}
 * `url` et `nom` du fond sont ajoutés par le serveur à la lecture (image ou vidéo de la galerie) : ils ne sont pas renvoyés.
 */

export const LIMITES = {
    diapositives: 30,
    textes: 8,
    texte: 300,
    duree: {min: 3, max: 600, defaut: 10},
    assombrir: {min: 0, max: 90},
};

export const POSITIONS = [
    {valeur: "haut", libelle: "En haut"},
    {valeur: "centre", libelle: "Au centre"},
    {valeur: "bas", libelle: "En bas"},
];

export const TAILLES = [
    {valeur: "petit", libelle: "Petit"},
    {valeur: "moyen", libelle: "Moyen"},
    {valeur: "grand", libelle: "Grand"},
    {valeur: "enorme", libelle: "Énorme"},
];

export const ANIMATIONS = [
    {valeur: "fondu", libelle: "Fondu"},
    {valeur: "glisse", libelle: "Glisse depuis la gauche"},
    {valeur: "monte", libelle: "Monte depuis le bas"},
    {valeur: "zoom", libelle: "Zoom qui rebondit"},
    {valeur: "machine", libelle: "Machine à écrire"},
];

export const CADRES = [
    {valeur: "aucun", libelle: "Sans cadre (ombre portée)"},
    {valeur: "sombre", libelle: "Bandeau sombre"},
    {valeur: "clair", libelle: "Bandeau clair"},
];

export const TYPES_FOND = [
    {valeur: "couleur", libelle: "Couleur"},
    {valeur: "anime", libelle: "Fond animé"},
    {valeur: "image", libelle: "Image de la galerie"},
    {valeur: "video", libelle: "Vidéo"},
];

/** Noms des @keyframes des textes (animations.css). */
export const NOMS_ANIMATIONS = {
    fondu: "aerith-pub-fondu", glisse: "aerith-pub-glisse", monte: "aerith-pub-monte",
    zoom: "aerith-pub-zoom", machine: "aerith-pub-machine", sortie: "aerith-pub-sortie",
};

/** Durée de l'entrée d'un texte (s) et de sa sortie. */
export const DUREE_ENTREE = 0.8;
export const DUREE_SORTIE = 0.6;

/** Identifiant court, unique en pratique dans un diaporama ([a-z0-9], comme l'exige le serveur). */
export const nouvelId = () => Math.random().toString(36).slice(2, 10) || "x";

const valeurs = liste => liste.map(o => o.valeur);
const parmi = (liste, valeur, defaut) => (valeurs(liste).includes(valeur) ? valeur : defaut);
const borne = (valeur, min, max, defaut) => {
    const n = Number(valeur);
    return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : defaut;
};
const couleur = (valeur, defaut) => (typeof valeur === "string" && /^#[0-9a-fA-F]{6}$/.test(valeur) ? valeur.toLowerCase() : defaut);
const dixiemes = n => Math.round(n * 10) / 10;

export function texteVide(debut = 0.5) {
    return {
        id: nouvelId(), texte: "Votre texte", debut, fin: null,
        position: "centre", taille: "grand", couleur: "#ffffff", animation: "fondu", cadre: "aucun",
    };
}

export function diapositiveVide() {
    return {
        id: nouvelId(), actif: true, duree: LIMITES.duree.defaut, video_entiere: false, assombrir: 0,
        fond: {type: "couleur", couleur: "#258f47", anime: null, asset_id: null},
        textes: [texteVide()],
    };
}

/** Une diapositive telle qu'elle peut s'afficher : chaque valeur absente, invalide ou hors bornes reprend sa valeur d'origine. */
export function normaliser(d) {
    const vide = diapositiveVide();
    const source = d && typeof d === "object" ? d : {};
    const fond = source.fond && typeof source.fond === "object" ? source.fond : {};
    const duree = Math.round(borne(source.duree, LIMITES.duree.min, LIMITES.duree.max, vide.duree));
    return {
        id: typeof source.id === "string" && /^[a-z0-9]{1,20}$/.test(source.id) ? source.id : nouvelId(),
        actif: source.actif !== false,
        duree,
        video_entiere: source.video_entiere === true,
        assombrir: Math.round(borne(source.assombrir, LIMITES.assombrir.min, LIMITES.assombrir.max, 0)),
        fond: {
            type: parmi(TYPES_FOND, fond.type, "couleur"),
            couleur: couleur(fond.couleur, vide.fond.couleur),
            anime: typeof fond.anime === "string" ? fond.anime : null,
            asset_id: Number.isInteger(fond.asset_id) ? fond.asset_id : null,
            ...(fond.url !== undefined ? {url: fond.url} : {}),
            ...(fond.nom !== undefined ? {nom: fond.nom} : {}),
        },
        textes: (Array.isArray(source.textes) ? source.textes : []).slice(0, LIMITES.textes).map(t => normaliserTexte(t, duree)),
    };
}

function normaliserTexte(t, duree) {
    const vide = texteVide();
    const source = t && typeof t === "object" ? t : {};
    const debut = dixiemes(borne(source.debut, 0, LIMITES.duree.max, 0));
    const fin = source.fin === null || source.fin === undefined || source.fin === "" ? null : dixiemes(borne(source.fin, 0, LIMITES.duree.max, duree));
    return {
        id: typeof source.id === "string" && /^[a-z0-9]{1,20}$/.test(source.id) ? source.id : nouvelId(),
        texte: typeof source.texte === "string" ? source.texte.slice(0, LIMITES.texte) : "",
        debut,
        fin: fin !== null && fin <= debut ? null : fin,
        position: parmi(POSITIONS, source.position, vide.position),
        taille: parmi(TAILLES, source.taille, vide.taille),
        couleur: couleur(source.couleur, vide.couleur),
        animation: parmi(ANIMATIONS, source.animation, vide.animation),
        cadre: parmi(CADRES, source.cadre, vide.cadre),
    };
}

/** Diapositives du diaporama envoyé par le serveur (ou {diapositives}), normalisées. */
export function diapositivesDe(publicite) {
    const liste = publicite && Array.isArray(publicite.diapositives) ? publicite.diapositives : [];
    return liste.slice(0, LIMITES.diapositives).map(normaliser);
}

/**
 * Diapositives qui passent à l'écran : actives, et dont le fond peut s'afficher (une image ou une vidéo supprimée de la
 * galerie n'a plus d'adresse : la diapositive garde alors son fond de couleur). `fondsAnimes` : identifiants connus.
 */
export function diapositivesAffichees(diapositives, fondsAnimes = []) {
    return diapositives.filter(d => d.actif).map(d => ({...d, fond: fondAffichable(d.fond, fondsAnimes)}));
}

export function fondAffichable(fond, fondsAnimes = []) {
    if (fond.type === "anime" && !fondsAnimes.includes(fond.anime)) return {...fond, type: "couleur"};
    if ((fond.type === "image" || fond.type === "video") && !fond.url) return {...fond, type: "couleur"};
    return fond;
}

/** Ce qui part au serveur : la forme décrite, sans l'adresse ni le nom ajoutés à la lecture. */
export function aEnvoyer(diapositives) {
    return diapositives.map(normaliser).map(d => ({
        ...d,
        fond: {type: d.fond.type, couleur: d.fond.couleur, anime: d.fond.anime, asset_id: d.fond.asset_id},
    }));
}

/** Instant (s) où le texte commence à disparaître ; null s'il reste jusqu'au bout de la diapositive. */
export function finDuTexte(texte, duree) {
    if (texte.fin === null || texte.fin === undefined) return null;
    return Math.min(texte.fin, duree);
}

/** Durée de l'entrée d'un texte : la machine à écrire tape à environ 25 caractères par seconde (3 s au plus). */
export function dureeEntree(texte) {
    if (texte.animation !== "machine") return DUREE_ENTREE;
    return Math.min(3, Math.max(0.4, texte.texte.length / 25));
}

/**
 * Animations CSS d'un texte : entrée à `debut`, sortie qui se termine à `fin` (si elle est donnée).
 * `noms` : les noms des @keyframes (NOMS_ANIMATIONS).
 * Rend {animationName, animationDuration, animationDelay, animationTimingFunction, animationFillMode} prêt pour `style`.
 */
export function animationsDuTexte(texte, duree, noms = NOMS_ANIMATIONS) {
    const entree = dureeEntree(texte);
    const fin = finDuTexte(texte, duree);
    const animations = [{nom: noms[texte.animation] || noms.fondu, duree: entree, delai: texte.debut,
        courbe: texte.animation === "machine" ? `steps(${Math.max(1, texte.texte.length)}, end)` : "cubic-bezier(.2,.8,.2,1)", remplissage: "both"}];
    if (fin !== null) {
        animations.push({nom: noms.sortie, duree: DUREE_SORTIE, delai: Math.max(texte.debut + entree, fin - DUREE_SORTIE), courbe: "ease-in", remplissage: "forwards"});
    }
    return {
        animationName: animations.map(a => a.nom).join(", "),
        animationDuration: animations.map(a => a.duree + "s").join(", "),
        animationDelay: animations.map(a => dixiemes(a.delai) + "s").join(", "),
        animationTimingFunction: animations.map(a => a.courbe).join(", "),
        animationFillMode: animations.map(a => a.remplissage).join(", "),
    };
}

/** Index de la diapositive suivante (on boucle). */
export const suivante = (index, nombre) => (nombre ? (index + 1) % nombre : 0);

/** Déplace un élément d'une liste (vers le haut : -1, vers le bas : +1), sans sortir de la liste. */
export function deplacer(liste, index, sens) {
    const cible = index + sens;
    if (cible < 0 || cible >= liste.length) return liste;
    const copie = liste.slice();
    [copie[index], copie[cible]] = [copie[cible], copie[index]];
    return copie;
}
